import { UserRoles, type UserRole } from 'api/users/types';
import { type CharityDocument } from 'features/documents/types';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

import {
  type CharityVettingFormValues,
  type CountryCode,
  type SetVettingRoleRequest,
  type StartCharityVettingRequest,
  type SubmitUnwantedFoodType,
  type UnwantedFoodType,
  type UpdateCharityVettingRequest,
  type VettingDocumentUpload,
  type VettingRequestData,
} from '../types/charityVettingTypes';

import { unwantedFoodItemsToCategories } from './formHandlers';

type CharityOnboarding = CharityVettingFormValues['charityOnboarding'];

/** A string is "present" only if it has non-blank content. */
const nonBlank = (value: string | undefined | null): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/** The API types registry ids as strings; the form stores number | null. */
const numToStr = (value: number | null | undefined): string | undefined =>
  value === null || value === undefined ? undefined : String(value);

/**
 * The form stores the phone in MuiTelInput's display format (spaced); the backend
 * wants clean E.164. Falls back to the raw value if it can't be parsed.
 */
const toE164 = (phoneNumber: string | undefined): string | undefined => {
  if (!nonBlank(phoneNumber)) {
    return undefined;
  }
  return parsePhoneNumberFromString(phoneNumber)?.number ?? phoneNumber;
};

/** Drop keys whose value is undefined so the update's addition-mode leaves them untouched. */
const compact = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;

/**
 * Maps the form's `charityOnboarding` to the stepped-vetting update payload,
 * translating the field-name differences (food-safety booleans, `pliCover`→`pli`,
 * `unwantedFoodItems`→`unwantedFoodCategories`, numeric registry ids→strings) and
 * omitting absent/blank values. The update is addition-mode, so sending the full
 * set of currently-known values on each step is safe (nothing is cleared) and lets
 * back-navigation edits re-sync naturally.
 */
export const toVettingPatch = (
  charityOnboarding: CharityOnboarding,
): UpdateCharityVettingRequest => {
  const co = charityOnboarding;
  return compact<UpdateCharityVettingRequest>({
    // Identity
    charityName: nonBlank(co.charityName) ? co.charityName : undefined,
    email: nonBlank(co.email) ? co.email : undefined,
    countryIsoCode: co.countryIsoCode ?? undefined,
    charityRegistrationId: nonBlank(co.charityRegistrationId)
      ? co.charityRegistrationId
      : undefined,
    address: nonBlank(co.address) ? co.address : undefined,
    extraAddressLine: nonBlank(co.extraAddressLine) ? co.extraAddressLine : undefined,
    postalCode: nonBlank(co.postalCode) ? co.postalCode : undefined,
    city: nonBlank(co.city) ? co.city : undefined,
    // Contact
    phoneNumber: toE164(co.phoneNumber),
    pointOfContactName: nonBlank(co.pointOfContactName) ? co.pointOfContactName : undefined,
    // Organisation
    isCompany: co.isCompany,
    companyRegistrationNumber: nonBlank(co.companyRegistrationNumber)
      ? co.companyRegistrationNumber
      : undefined,
    organizationDescription: nonBlank(co.organizationDescription)
      ? co.organizationDescription
      : undefined,
    charityType: co.charityType ?? undefined,
    socialMediaAccountLinks: co.socialMediaAccountLinks?.length
      ? co.socialMediaAccountLinks
      : undefined,
    // Food safety / hygiene (form name → spec name)
    isFsaRated: co.isFsaRated,
    hasHygieneCertificate: co.hasHygieneCertificate,
    haccp: co.haccp,
    preparationKitchen: co.preparationKitchen,
    isFoodTrained: co.staffTrainedInFoodHygiene,
    unwantedFoodCategories:
      co.acceptsAllFood || co.acceptsAllFoodAndNonFood || co.acceptsAllNonFood
        ? []
        : co.unwantedFoodItems?.length
          ? unwantedFoodItemsToCategories(co.unwantedFoodItems)
          : undefined,
    additionalUnwantedFoodCategories:
      co.acceptsAllFood || co.acceptsAllFoodAndNonFood || co.acceptsAllNonFood
        ? []
        : co.additionalUnwantedFoodCategories?.length
          ? unwantedFoodItemsToCategories(co.additionalUnwantedFoodCategories)
          : undefined,
    acceptsAllFood: co.acceptsAllFood,
    acceptsAllNonFood: co.acceptsAllNonFood,
    acceptsAllFoodAndNonFood: co.acceptsAllFoodAndNonFood,
    donationPreferences: co.donationPreferences,
    consumptionType: co.consumptionType,
    pli: co.pliCover?.value ? co.pliCover : undefined,
    // Fiscal / registry (numeric in form → string in API)
    nif: numToStr(co.nif),
    nipc: numToStr(co.nipc),
    fiscalBenefitsArticle: co.fiscalBenefitsArticle,
    cif: numToStr(co.cif),
    donationCertificate: co.donationCertificate,
    rgseaa: numToStr(co.rgseaa),
    krs: numToStr(co.krs),
    nip: numToStr(co.nip),
    siren: numToStr(co.siren),
    rna: numToStr(co.rna),
    isPublicBenefitOrg: co.isPublicBenefitOrg,
    // FR-only
    organisationPurpose: co.organisationPurpose,
    organisationSpecificType: co.organisationSpecificType,
    organisationOtherOption: co.organisationOtherOption,
    decreePublicationDate: nonBlank(co.decreePublicationDate)
      ? co.decreePublicationDate
      : undefined,
    officialJournalPublicationDate: nonBlank(co.officialJournalPublicationDate)
      ? co.officialJournalPublicationDate
      : undefined,
    arretePublicationDate: nonBlank(co.arretePublicationDate)
      ? co.arretePublicationDate
      : undefined,
    agrementDate: nonBlank(co.agrementDate) ? co.agrementDate : undefined,
    organisationSituation: nonBlank(co.organisationSituation)
      ? co.organisationSituation
      : undefined,
    isAuthorisedFoodAid: co.isAuthorisedFoodAid,
  });
};

/** Step 1 payload — required identity fields plus any optional contact fields collected. */
export const buildStartPayload = (
  charityOnboarding: CharityOnboarding,
): StartCharityVettingRequest => {
  const co = charityOnboarding;
  return {
    charityName: co.charityName,
    email: co.email,
    countryIsoCode: co.countryIsoCode as CountryCode,
    ...compact({
      address: nonBlank(co.address) ? co.address : undefined,
      postalCode: nonBlank(co.postalCode) ? co.postalCode : undefined,
      city: nonBlank(co.city) ? co.city : undefined,
      phoneNumber: toE164(co.phoneNumber),
      pointOfContactName: nonBlank(co.pointOfContactName) ? co.pointOfContactName : undefined,
    }),
  };
};

/** Maps the legal-representative answer to a charity role. */
export const buildRolePayload = (
  isLegalRepresentative: boolean | undefined,
): SetVettingRoleRequest => {
  const role: UserRole = isLegalRepresentative
    ? UserRoles.LegalRepresentative
    : UserRoles.Coordinator;
  return { role };
};

/** Server registry ids are strings; the form stores number | null. */
const strToNum = (value: string | undefined): number | undefined => {
  if (value === undefined || value.trim() === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const categoriesToUnwantedFoodItems = (
  categories: SubmitUnwantedFoodType[] | undefined,
): UnwantedFoodType[] | undefined =>
  categories?.map((category) => ({
    label: category.label,
    code: typeof category.label === 'string' ? category.label : '',
    active: category.active,
  }));

/**
 * Inverse of `toVettingPatch`: maps a persisted `VettingRequestData` (server
 * field names/types) back to the form's `charityOnboarding` shape, so a resumed
 * session (incl. cross-device) shows the saved answers. Only defined fields are
 * returned; the caller merges these over the form defaults.
 */
export const fromVettingRequestData = (data: VettingRequestData): Partial<CharityOnboarding> => {
  const result: Partial<CharityOnboarding> = compact<Partial<CharityOnboarding>>({
    // Identity
    charityName: data.charityName,
    email: data.email,
    countryIsoCode: data.countryIsoCode,
    charityRegistrationId: data.charityRegistrationId,
    address: data.address,
    extraAddressLine: data.extraAddressLine,
    postalCode: data.postalCode,
    city: data.city,
    // Contact
    phoneNumber: data.phoneNumber,
    pointOfContactName: data.pointOfContactName,
    // Organisation
    isCompany: data.isCompany,
    companyRegistrationNumber: data.companyRegistrationNumber,
    organizationDescription: data.organizationDescription,
    charityType: data.charityType,
    socialMediaAccountLinks: data.socialMediaAccountLinks,
    // Food safety / hygiene (spec name → form name)
    isFsaRated: data.isFsaRated,
    hasHygieneCertificate: data.hasHygieneCertificate,
    haccp: data.haccp,
    preparationKitchen: data.preparationKitchen,
    staffTrainedInFoodHygiene: data.isFoodTrained,
    // Food acceptance / donations
    unwantedFoodItems: categoriesToUnwantedFoodItems(data.unwantedFoodCategories),
    additionalUnwantedFoodCategories: categoriesToUnwantedFoodItems(
      data.additionalUnwantedFoodCategories,
    ),
    acceptsAllFood: data.acceptsAllFood,
    acceptsAllNonFood: data.acceptsAllNonFood,
    acceptsAllFoodAndNonFood: data.acceptsAllFoodAndNonFood,
    donationPreferences: data.donationPreferences,
    consumptionType: data.consumptionType,
    pliCover: data.pli,
    // Fiscal / registry (string in API → number in form)
    nif: strToNum(data.nif),
    nipc: strToNum(data.nipc),
    fiscalBenefitsArticle: data.fiscalBenefitsArticle,
    cif: strToNum(data.cif),
    donationCertificate: data.donationCertificate,
    rgseaa: strToNum(data.rgseaa),
    krs: strToNum(data.krs),
    nip: strToNum(data.nip),
    siren: strToNum(data.siren),
    rna: strToNum(data.rna),
    isPublicBenefitOrg: data.isPublicBenefitOrg,
    // FR-only
    organisationPurpose: data.organisationPurpose,
    organisationSpecificType: data.organisationSpecificType,
    organisationOtherOption: data.organisationOtherOption,
    decreePublicationDate: data.decreePublicationDate,
    officialJournalPublicationDate: data.officialJournalPublicationDate,
    arretePublicationDate: data.arretePublicationDate,
    agrementDate: data.agrementDate,
    organisationSituation: data.organisationSituation,
    isAuthorisedFoodAid: data.isAuthorisedFoodAid,
  });
  return result;
};

export const fromCharityDocuments = (
  documents: CharityDocument[],
): Partial<Record<CharityDocument['type'], VettingDocumentUpload>> =>
  Object.fromEntries(
    documents.map((document) => [
      document.type,
      { documentId: document.id, fileName: document.name },
    ]),
  );
